import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import * as XLSX from 'xlsx'
import { Grid, GridColumn } from '@progress/kendo-react-grid'
import { orderBy, filterBy } from '@progress/kendo-data-query'
import { DatePicker } from '@progress/kendo-react-dateinputs'
import { Button } from '@progress/kendo-react-buttons'
import { FaFileExcel, FaSearch } from 'react-icons/fa'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LabelList,
} from 'recharts'
import { getCurrentUserEmployee } from '../../data/employeeData'
import { appendEmployeeIds } from '../../data/reportUserScopeData'
import ReportUserFilter, { useReportUserFilter } from '../../components/ReportUserFilter/ReportUserFilter'
import { useToast } from '../../contexts/ToastContext'
import './QuoteLostReport.css'

interface QuoteLostReasonSummary {
  quoteLostReasonId: number | null
  reason: string
  quoteCount: number
  quoteValue: number
  countPct: number
  valuePct: number
}

interface QuoteLostMonth {
  monthStart: string
  month: string
  quoteCount: number
  quoteValue: number
}

interface QuoteLostDetail {
  quoteId: number
  reference: string
  lostDate: string | null
  reason: string
  client: string | null
  site: string | null
  worktype: string | null
  description: string | null
  cost: number
  assignedTo: string | null
  createdBy: string | null
}

interface QuoteLostReport {
  totalQuotesLost: number
  totalLostValue: number
  reasons: QuoteLostReasonSummary[]
  months: QuoteLostMonth[]
  quotes: QuoteLostDetail[]
}

const EMPTY_REPORT: QuoteLostReport = {
  totalQuotesLost: 0,
  totalLostValue: 0,
  reasons: [],
  months: [],
  quotes: [],
}

// Full precision for tiles and tooltips; the compact form keeps axis ticks
// readable at chart width.
const formatCurrency = (value: number) =>
  `£${(Number(value) || 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`

const formatCompactCurrency = (value: number) => {
  const amount = Number(value) || 0
  if (amount >= 1_000_000) return `£${(amount / 1_000_000).toFixed(1)}m`
  if (amount >= 1_000) return `£${Math.round(amount / 1_000)}k`
  return `£${Math.round(amount)}`
}

const formatDate = (value: string | null) =>
  value ? new Date(value).toLocaleDateString('en-GB') : ''

// Recessive ink for axes - deliberately not a series colour, so axis text never
// reads as an identity.
const axisTick = { fontSize: '9pt', fill: '#5a6472' }

// Every chart here carries a single series, so colour is not doing identity
// work: it is consistent by measure across the page. Counts are the portal
// navy, money is the same green the grids already use for financial columns.
// Both clear 3:1 against the white card, and separate at deltaE 23.5 under
// deuteranopia.
const COLOUR_COUNT = '#1e2a4e'
const COLOUR_VALUE = '#217346'

// Reason names run long ("Price - lost to competitor") and collide with the
// plot on a category axis. The tooltip still shows the full name.
const truncateCategory = (value: string) =>
  typeof value === 'string' && value.length > 16 ? `${value.slice(0, 15)}…` : value

function QuoteLostReport() {
  const navigate = useNavigate()
  const { showToast } = useToast()

  const [startDate, setStartDate] = useState<Date | null>(new Date(new Date().getFullYear(), 0, 1))
  const [endDate, setEndDate] = useState<Date | null>(new Date())
  const [report, setReport] = useState<QuoteLostReport>(EMPTY_REPORT)
  const [hasRun, setHasRun] = useState(false)
  const [loading, setLoading] = useState(false)
  const [hasPermission, setHasPermission] = useState(false)
  // Loaded once permission is confirmed, so an unpermitted user does not draw
  // a 403 on the way to being redirected.
  const users = useReportUserFilter(hasPermission)

  const [sort, setSort] = useState<any[]>([{ field: 'lostDate', dir: 'desc' }])
  const [filter, setFilter] = useState<any>(null)
  const [skip, setSkip] = useState(0)
  const [pageSize] = useState(25)

  const apiURL = process.env.REACT_APP_API_URL

  useEffect(() => {
    const checkPermissions = async () => {
      try {
        const currentUser = await getCurrentUserEmployee()
        if (currentUser && currentUser.permissions) {
          // An administrator gets in without the report role being assigned,
          // matching the policy the API endpoint is trimmed with.
          const permitted = currentUser.permissions.some(
            (p) =>
              p.assigned &&
              ((p.area === 'Portal' && p.permission === 'Administrator') ||
                (p.area === 'Reports' && p.permission === 'QuoteLost'))
          )
          setHasPermission(permitted)
          if (!permitted) {
            showToast('You do not have permission to access this page', 'error')
            navigate('/')
          }
        } else {
          showToast('Unable to verify permissions', 'error')
          navigate('/')
        }
      } catch (error) {
        console.error('Error checking permissions:', error)
        showToast('Error verifying permissions', 'error')
        navigate('/')
      }
    }
    checkPermissions()
  }, [navigate, showToast])

  const fetchData = useCallback(async () => {
    if (!startDate || !endDate) {
      showToast('Please select both start and end dates', 'info')
      return
    }
    if (startDate > endDate) {
      showToast('Start date must be before end date', 'error')
      return
    }
    try {
      setLoading(true)
      const token = localStorage.getItem('authToken')
      if (!token) throw new Error('Authorization token not found')

      // Built from the local date parts rather than toISOString(), which would
      // shift a UK date back a day over BST.
      const toLocalDateString = (d: Date) =>
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

      const startStr = toLocalDateString(startDate)
      const endStr = toLocalDateString(endDate)
      // The whole report is filtered server-side - headline figures, charts and
      // percentages are all computed there, so narrowing only the grid would
      // leave the overview describing a different set of quotes.
      const query = appendEmployeeIds(
        new URLSearchParams({ startDate: startStr, endDate: endStr }),
        users.selectedIds
      )
      const url = `https://${apiURL}/Quotes/LostReport?${query}`

      const response = await axios.get<QuoteLostReport>(url, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token,
        },
      })
      setReport(response.data)
      setSkip(0)
      setHasRun(true)
      showToast(`Loaded ${response.data.totalQuotesLost} lost quotes`, 'success')
    } catch (error: any) {
      console.error('Error fetching lost quote data:', error)
      const msg = error.response?.data?.message || error.message || 'Error loading lost quote data'
      showToast(msg, 'error')
    } finally {
      setLoading(false)
    }
  }, [startDate, endDate, users.selectedIds, showToast, apiURL])

  const quotesFiltered = filter ? filterBy(report.quotes, filter) : report.quotes
  const quotesSorted = orderBy(quotesFiltered, sort)

  const totalLost = report.totalQuotesLost
  const totalValue = report.totalLostValue
  const averageValue = totalLost > 0 ? totalValue / totalLost : 0
  // Reasons arrive ordered by count, so the leader is the first row.
  const topReason = report.reasons[0]

  // Ranked bars read top-down, and Recharts draws a vertical layout bottom-up,
  // so the biggest has to go last.
  const reasonsByCount = [...report.reasons].sort((a, b) => a.quoteCount - b.quoteCount)
  const reasonsByValue = [...report.reasons].sort((a, b) => a.quoteValue - b.quoteValue)

  // A range inside one month has nothing to trend, so the chart is dropped
  // rather than drawn as a lone bar.
  const showTrend = report.months.length > 1
  // A long range would collide every month label against its neighbour, so
  // past a couple of years the axis thins out and the tooltip carries the rest.
  const monthTickInterval = report.months.length > 18
    ? Math.ceil(report.months.length / 12) - 1
    : 0

  const exportToExcel = () => {
    const exportData = quotesSorted.map((row) => ({
      'Quote Ref': row.reference,
      'Lost Date': formatDate(row.lostDate),
      'Lost Reason': row.reason,
      'Client': row.client || '',
      'Site': row.site || '',
      'Department': row.worktype || '',
      'Description': row.description || '',
      'Quote Value': Number(row.cost),
      'Assigned To': row.assignedTo || '',
      'Created By': row.createdBy || '',
    }))

    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.json_to_sheet(exportData),
      'Lost Quotes'
    )

    // The reason summary the charts are drawn from, so the export stands on its
    // own without anyone having to re-pivot the detail.
    const summaryData = report.reasons.map((row) => ({
      'Lost Reason': row.reason,
      'Quotes Lost': row.quoteCount,
      'Share of Quotes (%)': row.countPct,
      'Lost Value': Number(row.quoteValue),
      'Share of Value (%)': row.valuePct,
    }))
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.json_to_sheet(summaryData),
      'By Reason'
    )

    const assigneeSuffix = users.selectedNames.length === 1
      ? `_${users.selectedNames[0].replace(/[^a-zA-Z0-9]+/g, '_')}`
      : ''
    const fileName = `quote_lost_report_${startDate?.toISOString().split('T')[0]}_to_${endDate?.toISOString().split('T')[0]}${assigneeSuffix}.xlsx`
    XLSX.writeFile(workbook, fileName)
    showToast(`Exported ${exportData.length} lost quotes to ${fileName}`, 'success')
  }

  return (
    <div>
      <div style={{ padding: '1rem' }}>
        <div className="quote-lost-report__header">
          <h1>Quote Lost Report</h1>
        </div>

        {/* Filters */}
        <div className="quote-lost-report__filters">
          <div>
            <label className="quote-lost-report__filter-label">Lost From</label>
            <DatePicker
              value={startDate}
              onChange={(e) => setStartDate(e.value)}
              format="dd/MM/yyyy"
              style={{ width: '170px' }}
            />
          </div>
          <div>
            <label className="quote-lost-report__filter-label">Lost To</label>
            <DatePicker
              value={endDate}
              onChange={(e) => setEndDate(e.value)}
              format="dd/MM/yyyy"
              style={{ width: '170px' }}
            />
          </div>
          <ReportUserFilter
            scope={users.scope}
            value={users.selectedIds}
            onChange={users.setSelectedIds}
            disabled={loading}
            label="Assigned To"
            labelClassName="quote-lost-report__filter-label"
          />
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Button
              onClick={fetchData}
              themeColor="primary"
              disabled={loading || !hasPermission}
            >
              <FaSearch style={{ marginRight: '0.4rem' }} />
              {loading ? 'Loading...' : 'Run Report'}
            </Button>
            <Button
              onClick={() => {
                setStartDate(new Date(new Date().getFullYear(), 0, 1))
                setEndDate(new Date())
                users.reset()
              }}
              themeColor="base"
              disabled={loading}
            >
              Reset Filters
            </Button>
          </div>
        </div>

        {totalLost > 0 && (
          <>
            {/* Overview: the headline figures, then the two questions a lost
                quote review asks - which reason costs us the most jobs, and
                which costs us the most money - plus how the period is moving. */}
            <div className="quote-lost-report__kpis">
              <div className="quote-lost-report__kpi quote-lost-report__kpi--navy">
                <div className="quote-lost-report__kpi-label">Quotes Lost</div>
                <div className="quote-lost-report__kpi-value">{totalLost}</div>
              </div>
              <div className="quote-lost-report__kpi quote-lost-report__kpi--orange">
                <div className="quote-lost-report__kpi-label">Total Lost Value</div>
                <div className="quote-lost-report__kpi-value">{formatCurrency(totalValue)}</div>
              </div>
              <div className="quote-lost-report__kpi quote-lost-report__kpi--navy">
                <div className="quote-lost-report__kpi-label">Average Lost Value</div>
                <div className="quote-lost-report__kpi-value">{formatCurrency(averageValue)}</div>
              </div>
              <div className="quote-lost-report__kpi quote-lost-report__kpi--green">
                <div className="quote-lost-report__kpi-label">Top Reason</div>
                <div className="quote-lost-report__kpi-value quote-lost-report__kpi-value--text">
                  {topReason ? topReason.reason : '-'}
                </div>
                {topReason && (
                  <div className="quote-lost-report__kpi-sub">
                    {topReason.quoteCount} quotes · {topReason.countPct.toFixed(1)}% of losses
                  </div>
                )}
              </div>
            </div>

            <div className="quote-lost-report__charts">
              <div className="quote-lost-report__chart">
                <h3>Quotes Lost by Reason</h3>
                <ResponsiveContainer width="100%" height={320}>
                  {/* One series, so the title names it and no legend is needed. */}
                  <BarChart
                    data={reasonsByCount}
                    layout="vertical"
                    margin={{ top: 5, right: 40, left: 10, bottom: 5 }}
                  >
                    <CartesianGrid stroke="#e9ecef" horizontal={false} />
                    <XAxis type="number" tick={axisTick} allowDecimals={false} />
                    <YAxis
                      type="category"
                      dataKey="reason"
                      tick={axisTick}
                      tickFormatter={truncateCategory}
                      width={150}
                    />
                    <Tooltip
                      formatter={(value: any, _name: any, entry: any) => [
                        `${value} quotes (${entry?.payload?.countPct?.toFixed(1)}%)`,
                        'Quotes Lost',
                      ]}
                    />
                    <Bar
                      dataKey="quoteCount"
                      name="Quotes Lost"
                      fill={COLOUR_COUNT}
                      radius={[0, 4, 4, 0]}
                    >
                      <LabelList
                        dataKey="quoteCount"
                        position="right"
                        style={{ fontSize: '9pt', fill: '#5a6472' }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="quote-lost-report__chart">
                <h3>Lost Value by Reason</h3>
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart
                    data={reasonsByValue}
                    layout="vertical"
                    margin={{ top: 5, right: 60, left: 10, bottom: 5 }}
                  >
                    <CartesianGrid stroke="#e9ecef" horizontal={false} />
                    <XAxis type="number" tick={axisTick} tickFormatter={formatCompactCurrency} />
                    <YAxis
                      type="category"
                      dataKey="reason"
                      tick={axisTick}
                      tickFormatter={truncateCategory}
                      width={150}
                    />
                    <Tooltip
                      formatter={(value: any, _name: any, entry: any) => [
                        `${formatCurrency(Number(value))} (${entry?.payload?.valuePct?.toFixed(1)}%)`,
                        'Lost Value',
                      ]}
                    />
                    <Bar
                      dataKey="quoteValue"
                      name="Lost Value"
                      fill={COLOUR_VALUE}
                      radius={[0, 4, 4, 0]}
                    >
                      <LabelList
                        dataKey="quoteValue"
                        position="right"
                        formatter={(value: any) => formatCompactCurrency(Number(value))}
                        style={{ fontSize: '9pt', fill: '#5a6472' }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {showTrend && (
                <div className="quote-lost-report__chart">
                  <h3>Quotes Lost by Month</h3>
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={report.months} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                      <CartesianGrid stroke="#e9ecef" vertical={false} />
                      <XAxis dataKey="month" tick={axisTick} interval={monthTickInterval} angle={-30} textAnchor="end" height={60} />
                      <YAxis tick={axisTick} allowDecimals={false} width={40} />
                      <Tooltip
                        formatter={(value: any, _name: any, entry: any) => [
                          `${value} quotes · ${formatCurrency(Number(entry?.payload?.quoteValue))}`,
                          'Quotes Lost',
                        ]}
                      />
                      <Bar
                        dataKey="quoteCount"
                        name="Quotes Lost"
                        fill={COLOUR_COUNT}
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {/* Detail */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
              <Button onClick={exportToExcel} themeColor="primary" data-excel="true">
                <FaFileExcel style={{ marginRight: '0.5rem' }} />
                Export to Excel
              </Button>
            </div>

            <div className="quote-lost-report-grid">
              <Grid
                data={quotesSorted.slice(skip, skip + pageSize)}
                sortable
                sort={sort}
                onSortChange={(e) => setSort(e.sort)}
                filterable
                filter={filter}
                onFilterChange={(e) => {
                  setFilter(e.filter)
                  setSkip(0)
                }}
                pageable
                pageSize={pageSize}
                skip={skip}
                onPageChange={(e) => setSkip(e.page.skip)}
                total={quotesFiltered.length}
                style={{ width: '100%' }}
              >
                <GridColumn field="reference" title="Quote Ref" width="120px" />
                <GridColumn
                  field="lostDate"
                  title="Lost Date"
                  width="130px"
                  filterable={false}
                  cell={(props) => <td>{formatDate(props.dataItem.lostDate)}</td>}
                />
                <GridColumn field="reason" title="Lost Reason" width="200px" />
                <GridColumn field="client" title="Client" width="200px" />
                <GridColumn field="site" title="Site" width="220px" />
                <GridColumn field="worktype" title="Department" width="160px" />
                <GridColumn field="description" title="Description" width="260px" />
                <GridColumn
                  field="cost"
                  title="Quote Value"
                  width="140px"
                  filter="numeric"
                  cell={(props) => <td>{formatCurrency(props.dataItem.cost)}</td>}
                />
                <GridColumn field="assignedTo" title="Assigned To" width="170px" />
              </Grid>
            </div>
          </>
        )}

        {/* Empty state */}
        {!loading && totalLost === 0 && (
          <div style={{ textAlign: 'center', padding: '4rem 2rem', color: '#666' }}>
            <p style={{ fontSize: '1.2rem', marginBottom: '0.5rem' }}>
              {hasRun
                ? users.selectedNames.length === 1
                  ? `No quotes assigned to ${users.selectedNames[0]} were lost in this date range`
                  : 'No quotes were lost in this date range'
                : 'No lost quote data'}
            </p>
            <p>Select a date range and click "Run Report" to see why quotes were lost</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default QuoteLostReport
